import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import type { ConversationListItem } from '@/entities/conversations';
import { useDialogsSectionRoute } from '@/pages/dialogs/model/useDialogsSectionRoute';

/**
 * Переход в карточку диалога из списка. В том же окне «Назад» вернёт на список с его текущими
 * query-параметрами (единый контракт возврата с дашбордом); в новом окне открывается прямой адрес
 * карточки. Совпавшие при поиске по содержимому фрагменты (реплика/комментарий) прокидываются в
 * query, чтобы карточка открылась на нужной вкладке и элементе.
 *
 * Запись открывается в том разделе, из которого её открыли: из раздела закреплённого пресета —
 * в его же адресе, из «Диалогов» — в адресе диалогов.
 */
export const useDialogRowClick = () => {
  const location = useLocation();
  const { listPath, buildRecordPath, navigateToRecord } = useDialogsSectionRoute();

  return useCallback(
    (item: ConversationListItem, openInNewWindow: boolean) => {
      const itemId = item.id?.toString();
      if (!itemId) return;

      const query: Record<string, string | undefined> = {};
      if (item.matchedPhraseId != null) query.phraseId = String(item.matchedPhraseId);
      if (item.matchedCommentId != null) {
        query.tab = 'comments';
        query.commentId = String(item.matchedCommentId);
      }

      if (openInNewWindow) {
        const basePath = buildRecordPath(itemId);
        const search = new URLSearchParams(
          Object.entries(query).filter(([, v]) => v != null) as [string, string][],
        ).toString();
        window.open(search ? `${basePath}?${search}` : basePath, '_blank');
        return;
      }

      // Разделённый экран (лейаут one-page): переключение записи не покидает детализацию,
      // поэтому выбранное представление переносится на новую запись — активный таб из `?tab=`
      // и секция атрибутов из URL hash. Совпадение поиска по содержимому имеет приоритет:
      // оно уводит на собственный таб/комментарий.
      const currentTab = new URLSearchParams(location.search).get('tab');
      if (currentTab && query.tab == null) query.tab = currentTab;

      navigateToRecord(itemId, { state: `${listPath}${location.search}`, query, hash: location.hash });
    },
    [navigateToRecord, buildRecordPath, listPath, location],
  );
};
